import { useEffect, useState } from "react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { supabase } from "@/integrations/supabase/client";

// Phase 3 (F6): adviser "Podcasts" surface.
//
// Reads via the existing `mobile_podcast_episodes()` SECURITY DEFINER RPC, which already
// returns published episodes (synced YouTube + publish-pipeline audio). Advisers are
// entitled to the podcast surface, so no new data path is needed — this is a lean reader.

interface AdviserEpisode {
  video_id: string;
  title: string;
  description: string | null;
  published_at: string | null;
  thumbnail_url: string | null;
}

export default function AdviserPodcasts() {
  const [episodes, setEpisodes] = useState<AdviserEpisode[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const { data, error } = await (supabase.rpc as any)("mobile_podcast_episodes");
        if (!cancelled) {
          if (error || !Array.isArray(data)) {
            setEpisodes([]);
          } else {
            setEpisodes(data as AdviserEpisode[]);
          }
        }
      } catch {
        if (!cancelled) setEpisodes([]);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-2xl font-bold">Podcasts</h2>
        <p className="text-muted-foreground">Our latest podcast episodes.</p>
      </div>

      {loading ? (
        <div className="text-center py-12 text-muted-foreground">Loading episodes...</div>
      ) : episodes.length === 0 ? (
        <div className="text-center py-12 text-muted-foreground">No episodes available yet.</div>
      ) : (
        <div className="grid gap-4 sm:grid-cols-2">
          {episodes.map((ep) => (
            <Card key={ep.video_id}>
              {ep.thumbnail_url && (
                <img
                  src={ep.thumbnail_url}
                  alt={ep.title}
                  className="w-full aspect-video object-cover rounded-t-lg"
                />
              )}
              <CardHeader>
                <CardTitle className="text-lg">{ep.title}</CardTitle>
                {ep.published_at && (
                  <CardDescription>
                    {new Date(ep.published_at).toLocaleDateString()}
                  </CardDescription>
                )}
              </CardHeader>
              <CardContent>
                <a
                  href={`https://www.youtube.com/watch?v=${ep.video_id}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-sm text-primary underline hover:no-underline"
                >
                  Watch on YouTube →
                </a>
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}
